// Analytics Dashboard Component with Charts
import { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { TrendingUp, TrendingDown, Users, Globe, Stethoscope, Calendar } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#F04E30', '#FF6B4D', '#58595B', '#6D6E71', '#FFA899', '#FFCCC2'];

function getAuth() {
  const token = localStorage.getItem("admin_token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

export default function AnalyticsDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState("30");
  const [loading, setLoading] = useState(true);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics?days=${period}`, getAuth());
      setAnalytics(res.data);
    } catch (err) {
      console.error("Failed to load analytics:", err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  if (loading || !analytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Analytics Overview</h2>
          <p className="text-sm text-slate-500">Track your leads, conversions, and performance</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Leads"
          value={analytics.total_leads}
          change={analytics.leads_change}
          icon={Users}
          color="orange"
        />
        <KPICard
          title="Countries"
          value={analytics.unique_countries}
          change={analytics.countries_change}
          icon={Globe}
          color="blue"
        />
        <KPICard
          title="Top Treatment"
          value={analytics.top_treatment}
          subtitle="Most requested"
          icon={Stethoscope}
          color="green"
        />
        <KPICard
          title="Conversion Rate"
          value={`${analytics.conversion_rate}%`}
          change={analytics.conversion_change}
          icon={TrendingUp}
          color="purple"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lead Trend Chart */}
        <Card className="p-6 bg-white rounded-xl border border-slate-100">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-orange-600" />
            Lead Trends
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={analytics.lead_trends}>
              <defs>
                <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F04E30" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#F04E30" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <Tooltip 
                contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }}
                labelStyle={{ fontWeight: 600 }}
              />
              <Area 
                type="monotone" 
                dataKey="leads" 
                stroke="#F04E30" 
                strokeWidth={2}
                fillOpacity={1} 
                fill="url(#colorLeads)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* Country Distribution */}
        <Card className="p-6 bg-white rounded-xl border border-slate-100">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Globe className="h-4 w-4 text-orange-600" />
            Leads by Country
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={analytics.country_distribution}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={2}
                dataKey="value"
                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                labelLine={false}
              >
                {analytics.country_distribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Treatment Distribution */}
      <Card className="p-6 bg-white rounded-xl border border-slate-100">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Stethoscope className="h-4 w-4 text-orange-600" />
          Leads by Treatment
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={analytics.treatment_distribution} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" />
            <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} stroke="#94a3b8" width={120} />
            <Tooltip 
              contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }}
            />
            <Bar dataKey="count" fill="#F04E30" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Lead Sources */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {analytics.lead_sources.map((source, i) => (
          <Card key={i} className="p-4 bg-white rounded-xl border border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-600">{source.name}</span>
              <Badge variant="outline" className="text-xs">{source.count}</Badge>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2">
              <div 
                className="bg-orange-600 h-2 rounded-full transition-all"
                style={{ width: `${source.percentage}%` }}
              />
            </div>
            <p className="text-xs text-slate-400 mt-1">{source.percentage}% of total</p>
          </Card>
        ))}
      </div>
    </div>
  );
}

function KPICard({ title, value, change, subtitle, icon: Icon, color }) {
  const isPositive = change > 0;
  const colorClasses = {
    orange: "bg-orange-100 text-orange-600",
    blue: "bg-blue-100 text-blue-600",
    green: "bg-green-100 text-green-600",
    purple: "bg-purple-100 text-purple-600",
  };

  return (
    <Card className="p-4 bg-white rounded-xl border border-slate-100">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        {change !== undefined && (
          <Badge 
            variant="outline" 
            className={`text-xs ${isPositive ? "text-green-600 border-green-200" : "text-red-600 border-red-200"}`}
          >
            {isPositive ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
            {Math.abs(change)}%
          </Badge>
        )}
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{subtitle || title}</p>
    </Card>
  );
}
